import { ConnectError } from '@connectrpc/connect';
import { ArtifactTypes } from '@mono/proto-clients';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { artifactClient } from '../lib/connect/clients';
/**
 * Map a tesoro `ProcessingStatus` enum value to the legacy string used by
 * thousand-sunny consumers ('pending'|'running'|'completed'|'failed'|'cancelled').
 * Keeps callers byte-compatible while we phase out the REST shape.
 */ function processingStatusToLegacy(s) {
    switch(s){
        case ArtifactTypes.ProcessingStatus.QUEUED:
            return 'pending';
        case ArtifactTypes.ProcessingStatus.RUNNING:
            return 'running';
        case ArtifactTypes.ProcessingStatus.COMPLETED:
            return 'completed';
        case ArtifactTypes.ProcessingStatus.FAILED:
            return 'failed';
        case ArtifactTypes.ProcessingStatus.CANCELLED:
            return 'cancelled';
        default:
            return 'none';
    }
}
/**
 * jobStatusQueryKey is shared between useJobStatus and any other code path
 * that wants to dedupe against the hook's React Query cache. ComposerInput's
 * submit-time poll loop uses queryClient.fetchQuery against this key so the
 * two pollers (the hook from each rendered JobStatus, plus the submit-time
 * waiter) collapse to one in-flight RPC per (job_id, second).
 */ export const jobStatusQueryKey = (jobId)=>[
        'jobStatus',
        jobId
    ];
/**
 * Fetch job status from tesoro via Connect RPC. Replaces the previous
 * REST call to `/api/jobs/:id/status`. Errors are surfaced as Error
 * instances with messages aligned with what the old fetch path threw,
 * so the consumer's retry/refetch policies don't need to change.
 *
 * Exported so the composer's submit-time poll can share the same
 * fetcher + queryKey as useJobStatus, deduping concurrent reads.
 */ export async function fetchJobStatus(jobId) {
    try {
        const resp = await artifactClient.getProcessingJob({
            jobId
        });
        if (!resp.job) {
            throw new Error('Job not found');
        }
        return {
            job_id: resp.job.jobId,
            status: processingStatusToLegacy(resp.job.status),
            progress: {
                percentage: Number(resp.job.progressPercent ?? 0),
                currentStep: resp.job.currentStep || undefined
            },
            estimatedRemainingSeconds: Number(resp.job.estimatedRemainingSeconds ?? 0) || undefined,
            error: resp.job.errorMessage || undefined
        };
    } catch (err) {
        if (err instanceof ConnectError) {
            // Match legacy 404 / 403 semantics so the consumer's retry guard
            // (failureCount → don't retry on 404/403) still works.
            if (err.code === 5 /* NotFound */ ) {
                throw new Error('Job not found (404)');
            }
            if (err.code === 7 /* PermissionDenied */ ) {
                throw new Error('You do not have permission to access this job (403)');
            }
        }
        throw err;
    }
}
/**
 * React hook for polling job status from tesoro (which fans out to
 * reverie). Polls until the job reaches a terminal state.
 */ export function useJobStatus(jobId, options = {}) {
    const { enabled = true, onComplete, onError, onCancel, pollInterval = 2000, timeout = 300000, fetcher } = options;
    // Track when polling started for timeout. Reset on jobId change so a
    // retry (RetryProcessing swaps the row's jobId to a fresh job) starts
    // its own timeout budget. Without the reset, the retried job inherits
    // the original's elapsed time and can stop polling prematurely.
    // previousStatusRef rides the same reset because the completion
    // callbacks ought to fire fresh per job — otherwise the hook could
    // skip onComplete on a retried job whose first fetch returned its
    // already-terminal status before any "running" state was observed.
    const pollStartTime = useRef(null);
    const previousStatusRef = useRef(null);
    useEffect(()=>{
        pollStartTime.current = null;
        previousStatusRef.current = null;
    }, [
        jobId
    ]);
    // Query for job status. queryKey lives in jobStatusQueryKey so submit-time
    // pollers in ComposerInput can share the same cache slot.
    const query = useQuery({
        queryKey: jobId ? jobStatusQueryKey(jobId) : [
            'jobStatus',
            null
        ],
        queryFn: ()=>{
            if (!jobId) {
                throw new Error('Job ID is required');
            }
            return fetcher ? fetcher(jobId) : fetchJobStatus(jobId);
        },
        enabled: enabled && !!jobId && jobId !== '' && jobId !== 'none',
        refetchInterval: (query)=>{
            // Don't poll if data is null/undefined
            if (!query.state.data) return false;
            const status = query.state.data.status;
            // Stop polling if job is in terminal state
            if (status === 'completed' || status === 'failed' || status === 'cancelled') {
                return false;
            }
            // A browser observation deadline does not finish the processor job.
            // Keep observing slowly so late success or failure reaches the row.
            if (pollStartTime.current !== null && Date.now() - pollStartTime.current > timeout) {
                return Math.max(pollInterval, 10000);
            }
            return pollInterval;
        },
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        retry: (failureCount, error)=>{
            // Don't retry on 404 (job not found) or 403 (forbidden)
            if (error instanceof Error) {
                if (error.message.includes('404') || error.message.includes('403')) {
                    return false;
                }
            }
            // Retry network errors up to 3 times
            return failureCount < 3;
        }
    });
    // Initialize poll start time when query starts
    useEffect(()=>{
        if (query.isFetching && !pollStartTime.current) {
            pollStartTime.current = Date.now();
        }
    }, [
        query.isFetching
    ]);
    // Handle status changes and invoke callbacks
    useEffect(()=>{
        if (!query.data) return;
        const currentStatus = query.data.status;
        const previousStatus = previousStatusRef.current;
        // Update previous status
        previousStatusRef.current = currentStatus;
        // Don't trigger callbacks if status hasn't changed
        if (currentStatus === previousStatus) return;
        // Trigger appropriate callback based on status
        if (currentStatus === 'completed' && onComplete) {
            onComplete(query.data);
        } else if (currentStatus === 'failed' && onError) {
            onError(query.data.error || 'Job failed');
        } else if (currentStatus === 'cancelled' && onCancel) {
            onCancel(query.data);
        }
    }, [
        query.data,
        onComplete,
        onError,
        onCancel
    ]);
    // Determine if currently polling
    const isPolling = query.isSuccess && query.data && (query.data.status === 'pending' || query.data.status === 'running');
    return {
        ...query,
        isPolling,
        progress: query.data?.progress,
        status: query.data?.status,
        estimatedRemainingSeconds: query.data?.estimatedRemainingSeconds
    };
}
/**
 * Cancel a running job via tesoro. Replaces the legacy
 * `DELETE /api/jobs/:id` call.
 */ export async function cancelJob(jobId) {
    try {
        await artifactClient.cancelProcessingJob({
            jobId
        });
    } catch (err) {
        if (err instanceof ConnectError) {
            if (err.code === 5 /* NotFound */ ) {
                throw new Error('Job not found');
            }
            if (err.code === 7 /* PermissionDenied */ ) {
                throw new Error('You do not have permission to cancel this job');
            }
        }
        throw err;
    }
}
